import {
  Navigate,
  createBrowserRouter,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import Layout from '../../components/Layout';
import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { PreviousLocationProvider } from '../../providers/PreviousLocationProvider';
import DesignRoute from '../../components/DesignRoute';
import RouteLoading from '../../components/RouteLoading';
import ErrorBoundary from '../../components/ErrorBoundary';
import { useUserCompaniesCurrentUserAdminState } from '../../services/user.service';
import { USER_ROLES } from '../../consts/userRoles';
import { NoAccess } from '../../components/NoAccess';
import { NoAccessV2 } from '../../components/NoAccess/V2';
import { workflowService } from '../../services/workflow.services';
import toast from 'react-hot-toast';
import RequireFeatureFlag from '../../components/RequireFeatureFlag';
import { FEATURE_FLAG_NAMES } from '../../hooks/useFeatureFlag';
import { lazyWithRetry } from '../../utils/lazyWithRetry';
import { projectPath } from '../../utils/projectPath';

const OutputTables = lazyWithRetry(() => import('../../pages/OutputTables'));
const Output = lazyWithRetry(() => import('../../pages/Output'));
const Reportings = lazyWithRetry(() => import('../../pages/Dashboard/Reportings'));
const SelectFiles = lazyWithRetry(() => import('../../pages/Dashboard/Reportings/SelectFiles'));
const FCAReport = lazyWithRetry(() => import('../../pages/Report/FCA'));
const CreateProject = lazyWithRetry(() => import('../../pages/Project/Create'));
const Project = lazyWithRetry(() => import('../../pages/Project'));
const Template = lazyWithRetry(() => import('../../pages/Project/Template'));
const Periods = lazyWithRetry(() => import('../../pages/Project/Periods'));
const DataExtraction = lazyWithRetry(() => import('../../pages/DataExtraction'));
const SourceFilesRoute = lazyWithRetry(() => import('../../pages/SourceFilesV2/SourceFilesRoute'));
const ESMAReport = lazyWithRetry(() => import('../../pages/Report/ESMA'));
const AddAIFs = lazyWithRetry(() => import('../../pages/Project/Quarters/AddAIFs'));
const AddFile = lazyWithRetry(() => import('../../pages/Project/Quarters/AddFile'));
const Jurisdictions = lazyWithRetry(() => import('../../pages/Project/Quarters/Jurisdictions'));
const JurisdictionsV2 = lazyWithRetry(() => import('../../pages/Project/Quarters/JurisdictionsV2'));
const AddAIFsV2 = lazyWithRetry(() => import('../../pages/Project/Quarters/AddAIFsV2'));
const ManageUsers = lazyWithRetry(() => import('../../pages/ManageUsers/index'));
const ActivityLog = lazyWithRetry(() => import('../../pages/ActivityLog'));
const FormPF = lazyWithRetry(() => import('../../pages/Report/FormPF'));
const IntegrationsPage = lazyWithRetry(() => import('../../pages/Integrations'));
const Setup = lazyWithRetry(() => import('../../pages/Setup'));
const ExtractionV2 = lazyWithRetry(() => import('../../pages/ExtractionV2'));
const TransformationV2 = lazyWithRetry(() => import('../../pages/TransformationV2'));
const ReportV2 = lazyWithRetry(() => import('../../pages/ReportV2'));
// Named export: keyed by project and period, so another report mounts fresh.
const ReportV2Page = lazyWithRetry(() =>
  import('../../pages/ReportV2').then(module => ({ default: module.ReportV2Page }))
);
const ProjectFunds = lazyWithRetry(() => import('../../pages/Project/Funds'));

const withPreviousLocation = element => (
  <PreviousLocationProvider>{element}</PreviousLocationProvider>
);

const AdminRoute = ({ children }) => {
  const { data: currentUserState, isLoading } = useUserCompaniesCurrentUserAdminState();

  if (isLoading) {
    return <RouteLoading />;
  }

  if (currentUserState?.role !== USER_ROLES.ADMIN && !currentUserState?.is_super_admin) {
    return <Navigate to="/project" replace />;
  }

  return children;
};

// The new design keeps the workspace pages inside the project list, beside its rail.
const InWorkspace = ({ view }) => {
  const [searchParams] = useSearchParams();
  const params = new URLSearchParams(searchParams);
  params.set('view', view);
  return <Navigate to={`/project?${params}`} replace />;
};

const GroupedQueryRedirect = ({ children }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  if (searchParams.get('type') !== 'grouped') return children;

  const rest = new URLSearchParams(searchParams);
  rest.delete('type');
  const query = rest.toString();

  return <Navigate to={`${projectPath(id, true)}${query ? `?${query}` : ''}`} replace />;
};

const WorkflowAccessRoute = ({ children, consolidated = false }) => {
  const { id, projectId, workflowId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const targetWorkflowId = workflowId || projectId || id;
  const isWorkflowGroup = consolidated || searchParams.has('jurisdictionId');
  /* The answer is kept, the question is still asked.
   *
   * `cacheTime: 0` meant every move between two pages of the SAME project threw the answer away and
   * asked again from nothing — and while it asked, `RouteLoading` covered the window. Walking a
   * period through its steps is a move like that at every step, so the run's own progress was
   * punctuated by a full white screen each time, with the breadcrumb blank.
   *
   * `staleTime` stays at zero: every navigation still asks, so access taken away is noticed at the
   * next move, exactly as before. What changes is that the previous answer is still in hand while
   * the new one is on its way, so the page renders instead of the screen going white — and if the
   * answer comes back 403 or 404, the effect below redirects just as it always did. */
  const { data, isLoading, isError, error } = useQuery(
    ['WORKFLOW_ACCESS_GUARD', targetWorkflowId, isWorkflowGroup],
    () =>
      isWorkflowGroup
        ? workflowService.getGroupedWorkflows(targetWorkflowId)
        : workflowService.getWorkflowById(targetWorkflowId),
    {
      cacheTime: 5 * 60 * 1000,
      enabled: !!targetWorkflowId,
      retry: false,
      staleTime: 0
    }
  );

  const shouldRedirect = isError && [403, 404].includes(error?.response?.status);

  useEffect(() => {
    if (shouldRedirect) {
      toast.error('You do not have access to that project.');
      navigate('/project', { replace: true });
    }
  }, [navigate, shouldRedirect]);

  if (!targetWorkflowId) {
    return children;
  }

  /* Only when there is no answer at all to go on. A re-check with last minute's answer in hand is
     not a reason to take the page away from the reader. */
  if (isLoading && !data) {
    return <RouteLoading />;
  }

  if (shouldRedirect) {
    return null;
  }

  return children;
};

/**
 * The extraction address, in whichever design the reader works in.
 *
 * The classic page is one page with three tabs; the redesign gave two of those tabs a page each.
 * So the tab in the query is what picks, and the third — lookup tables, which has no redesigned
 * page — stays on the classic one for everybody.
 */
const ExtractionTabRoute = () => {
  const [params] = useSearchParams();
  const tab = params.get('tab') || 'extraction';

  if (tab === 'transformation')
    return <DesignRoute v2={<TransformationV2 />} classic={<DataExtraction />} />;
  if (tab === 'extraction')
    return <DesignRoute v2={<ExtractionV2 />} classic={<DataExtraction />} />;

  return <DataExtraction />;
};

export const privateRoutes = createBrowserRouter([
  {
    path: '/activity-log',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <ErrorBoundary componentName="ActivityLog">
            <DesignRoute v2={<InWorkspace view="activity" />} classic={<ActivityLog />} />
          </ErrorBoundary>
        )
      }
    ]
  },
  {
    path: '/data-extraction/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <WorkflowAccessRoute>
            <ErrorBoundary componentName="DataExtraction">
              {/* Three tabs on the classic page, and two of them have a redesigned page of their
                  own. Lookup tables has none, so it stays classic in both designs — the stepper
                  says as much. */}
              <ExtractionTabRoute />
            </ErrorBoundary>
          </WorkflowAccessRoute>
        )
      }
    ]
  },
  {
    path: '/setup/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="Setup">
              <Setup />
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    // Redesigned Jurisdictions (opt-in, legacy untouched — the old route and its page stay exactly
    // as they were). Same container, same hook, same ESMAJurisdiction table; only the layout differs,
    // so the two can be compared side by side while this is behind the flag.
    path: '/jurisdictions-v2/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="JurisdictionsV2">
              <JurisdictionsV2 />
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    // Redesigned Jurisdictions (opt-in, legacy untouched — the old route and its page stay exactly
    // as they were). Same container, same hook, same ESMAJurisdiction table; only the layout differs,
    // so the two can be compared side by side while this is behind the flag.
    path: '/aifs-v2/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="AddAIFsV2">
              <AddAIFsV2 />
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    // New redesigned extraction page (opt-in, legacy untouched). Mounted INSIDE the
    // app Layout — same as the legacy extraction page — so the app's common chrome
    // (AppTopBar + WorkflowStepper + WorkflowToolbar) is the navigation/steps/action-bar.
    // Use the :id/:periodId path so that chrome populates from the workflow context;
    // the bare path renders the workspace with empty chrome.
    path: '/extraction-v2',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="ExtractionV2">
              <ExtractionV2 />
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    path: '/extraction-v2/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="ExtractionV2">
              <ExtractionV2 />
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    // New redesigned transformation page (opt-in, legacy untouched). Wired to api/transform; the
    // workflow/period in the path scope every query, so there is no bare variant of this route.
    path: '/transformation-v2/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="TransformationV2">
              <TransformationV2 />
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    // New redesigned Output page (opt-in, legacy untouched). Sibling of transformation-v2 but wired
    // to the output-transform layer; the workflow/period in the path scope every query.
    path: '/output/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="Output">
              <Output />
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    // New redesigned report page (opt-in, legacy untouched). Same Layout-mounted pattern as
    // extraction-v2 so the app chrome drives navigation; the :id/:periodId path populates it.
    path: '/report-v2',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="ReportV2">
              <ReportV2 />
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    path: '/report-v2/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.V2_UI} fallback="/project">
            <ErrorBoundary componentName="ReportV2">
              <ReportV2Page />
            </ErrorBoundary>
          </RequireFeatureFlag>
        )
      }
    ]
  },
  {
    path: '/',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        path: 'project',
        children: [
          {
            index: true,
            element: (
              <ErrorBoundary componentName="Project">
                <Project />
              </ErrorBoundary>
            )
          },
          {
            path: 'consolidated/:id',
            element: (
              <WorkflowAccessRoute consolidated>
                <Periods isGrouped />
              </WorkflowAccessRoute>
            )
          },
          {
            path: 'quaters/:id',
            children: [
              {
                index: true,
                element: (
                  <GroupedQueryRedirect>
                    <WorkflowAccessRoute>
                      <Periods />
                    </WorkflowAccessRoute>
                  </GroupedQueryRedirect>
                )
              },
              {
                path: ':periodId/add-aifs',
                children: [
                  {
                    index: true,
                    element: (
                      <WorkflowAccessRoute>
                        <DesignRoute v2={<AddAIFsV2 />} classic={<AddAIFs />} />
                      </WorkflowAccessRoute>
                    )
                  }
                ]
              },
              {
                path: ':periodId/add-file',
                children: [
                  {
                    index: true,
                    element: (
                      <WorkflowAccessRoute>
                        <AddFile />
                      </WorkflowAccessRoute>
                    )
                  }
                ]
              },
              {
                path: ':periodId/upload-files',
                children: [
                  {
                    index: true,
                    element: (
                      <WorkflowAccessRoute>
                        <SourceFilesRoute />
                      </WorkflowAccessRoute>
                    )
                  }
                ]
              },
              {
                path: ':periodId/jurisdictions',
                element: (
                  <WorkflowAccessRoute>
                    <DesignRoute v2={<JurisdictionsV2 />} classic={<Jurisdictions />} />
                  </WorkflowAccessRoute>
                )
              }
            ]
          },
          {
            path: ':projectId',
            children: [
              {
                index: true,
                element: (
                  <WorkflowAccessRoute>
                    <Template />
                  </WorkflowAccessRoute>
                )
              },
              {
                path: 'funds',
                element: (
                  <WorkflowAccessRoute>
                    <ProjectFunds />
                  </WorkflowAccessRoute>
                )
              },
              {
                path: 'jurisdictions',
                element: (
                  <WorkflowAccessRoute>
                    <DesignRoute v2={<JurisdictionsV2 />} classic={<Jurisdictions />} />
                  </WorkflowAccessRoute>
                )
              },
              {
                path: 'create',
                element: (
                  <WorkflowAccessRoute>
                    <CreateProject isCreate={true} />
                  </WorkflowAccessRoute>
                )
              },
              {
                path: ':templateId',
                element: (
                  <WorkflowAccessRoute>
                    <CreateProject />
                  </WorkflowAccessRoute>
                )
              }
            ]
          }
        ]
      },

      {
        path: 'dashboard',
        element: <Layout />,
        children: [
          {
            index: true,
            // element: <Dashboard />,
            element: <Navigate to="/project" />
          },
          {
            path: 'reportings',
            children: [
              {
                index: true,
                element: (
                  <ErrorBoundary componentName="Reportings">
                    <Reportings />
                  </ErrorBoundary>
                )
              },
              {
                path: 'report',
                children: [
                  {
                    path: ':reportId',
                    element: (
                      <ErrorBoundary componentName="FCAReport">
                        <FCAReport />
                      </ErrorBoundary>
                    )
                  }
                ]
              },
              {
                path: ':integration',
                children: [
                  {
                    path: 'files',
                    element: <SelectFiles />
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: `report/fca/:reportingMemberState/:workflowId/:periodId`,
        element: (
          <WorkflowAccessRoute>
            <ErrorBoundary componentName="FCAReport">
              <FCAReport />
            </ErrorBoundary>
          </WorkflowAccessRoute>
        )
      },
      {
        path: `report/esma/:reportingMemberState/:workflowId/:periodId`,
        element: (
          <WorkflowAccessRoute>
            <ErrorBoundary
              componentName="ESMAReport"
              title="We couldn't display this report."
              description="Try opening it again. If the problem continues, contact support."
            >
              <ESMAReport />
            </ErrorBoundary>
          </WorkflowAccessRoute>
        )
      },
      {
        path: `report/pf/:reportingMemberState/:workflowId/:periodId`,
        element: (
          <WorkflowAccessRoute>
            <ErrorBoundary componentName="FormPF">
              <FormPF />
            </ErrorBoundary>
          </WorkflowAccessRoute>
        )
      },
      {
        path: 'integrations',
        element: (
          <DesignRoute v2={<InWorkspace view="integrations" />} classic={<IntegrationsPage />} />
        )
      },
      {
        path: 'output-tables/:projectId/:periodId',
        element: (
          <WorkflowAccessRoute>
            <ErrorBoundary componentName="OutputTables">
              <DesignRoute v2={<Output />} classic={<OutputTables />} />
            </ErrorBoundary>
          </WorkflowAccessRoute>
        )
      },
      {
        path: 'templates',
        element: <>Saved templates</>
      },
      {
        path: 'Payment',
        element: <>Payment</>
      },
      {
        path: 'help',
        element: <>Help</>
      },
      {
        path: 'access-denied',
        element: <DesignRoute v2={<NoAccessV2 />} classic={<NoAccess />} />
      },
      {
        path: 'settings',
        element: (
          <AdminRoute>
            <DesignRoute v2={<InWorkspace view="settings" />} classic={<ManageUsers />} />
          </AdminRoute>
        )
      },
      {
        path: '*',
        element: <Navigate to="/project" />
      },
      {
        index: true,
        element: <Navigate to="/project" />
      },
      {}
    ]
  },
  {
    path: '*',
    element: <Navigate to="/project" />
  }
]);
